/**
 * Podzavihek Poraba (docs/ARHITEKTURA.md, 15.5): amortizacija, pripisana
 * projektom po mesecih leta; »prej« zajema zacetno stanje in pretekla leta.
 * Klik na znesek meseca odpre knjizbe.
 */
import type { KnjizbaDelitve, PorabaProjektov, VrsticaPorabe } from '@koda/shared';
import { useCallback, useEffect, useState, type JSX } from 'react';

import { api } from '../../lib/api';
import { mesecZaPrikaz, useBesedilaProjektov } from '../../lib/besedila-projektov';
import { znesek } from '../../lib/format';
import { OpozorilaUvoza } from '../OpozorilaUvoza';
import { KnjizbeProjekta } from './KnjizbeProjekta';

export function PorabaProjektovStranke({ podjetje }: { podjetje: string }): JSX.Element {
  const b = useBesedilaProjektov();
  const [poraba, setPoraba] = useState<PorabaProjektov | null>(null);
  const [napaka, setNapaka] = useState<string | null>(null);
  const [odprto, setOdprto] = useState<{ opis: string; knjizbe: KnjizbaDelitve[] } | null>(null);

  const preberi = useCallback(
    (leto?: number): void => {
      api
        .projektiPoraba(podjetje, leto)
        .then((p) => {
          setPoraba(p);
          setNapaka(null);
        })
        .catch((e: unknown) => {
          setNapaka(e instanceof Error ? e.message : b.napakaBranja);
        });
    },
    [podjetje, b.napakaBranja],
  );
  useEffect(() => {
    preberi();
  }, [preberi]);

  const odpri = (v: VrsticaPorabe, mesec: number): void => {
    if (poraba === null) return;
    const opis = `${v.naziv ?? b.brezProjekta}, ${b.meseci[mesec - 1] ?? ''} ${String(poraba.leto)}`;
    api
      .projektiPorabaKnjizbe(podjetje, poraba.leto, mesec, v.projekt)
      .then((knjizbe) => {
        setOdprto({ opis, knjizbe });
      })
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : b.napakaBranja);
      });
  };

  if (napaka !== null) {
    return (
      <p className="napaka" role="alert">
        {napaka}
      </p>
    );
  }
  if (poraba === null) return <p className="drobno">{b.berem}</p>;
  const odprtMesec = poraba.odprtMesec === null ? null : Number(poraba.odprtMesec.slice(5, 7));

  return (
    <>
      <section className="kartica">
        <p className="podnaslov">{b.porabaUvod}</p>
        <label>
          {b.leto}{' '}
          <select
            aria-label={b.leto}
            value={poraba.leto}
            onChange={(d) => {
              setOdprto(null);
              preberi(Number(d.target.value));
            }}
          >
            {poraba.leta.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </label>
        <OpozorilaUvoza opozorila={poraba.opozorila} />
        {poraba.vrstice.length === 0 ? (
          <p className="drobno">{b.niPodatkov}</p>
        ) : (
          <div className="tabela-ovoj">
            <table>
              <thead>
                <tr>
                  <th>{b.projekt}</th>
                  <th className="stevilka">{b.prej}</th>
                  {b.meseci.map((m, i) => (
                    <th key={m} className="stevilka">
                      {m}
                      {odprtMesec === i + 1 && '*'}
                    </th>
                  ))}
                  <th className="stevilka">{b.skupaj}</th>
                </tr>
              </thead>
              <tbody>
                {poraba.vrstice.map((v) => (
                  <tr key={v.projekt ?? 'brez'}>
                    <td>{v.naziv ?? <em>{b.brezProjekta}</em>}</td>
                    <td className="stevilka">{znesek(v.prej)}</td>
                    {v.meseci.map((z, i) => (
                      <td key={String(i)} className="stevilka">
                        {Number(z) === 0 ? (
                          <span className="drobno">—</span>
                        ) : (
                          <button
                            type="button"
                            className="gumb-besedilo"
                            aria-label={`${v.naziv ?? b.brezProjekta} ${b.meseci[i] ?? ''}`}
                            onClick={() => {
                              odpri(v, i + 1);
                            }}
                          >
                            {znesek(z)}
                          </button>
                        )}
                      </td>
                    ))}
                    <td className="stevilka">
                      <strong>{znesek(v.skupaj)}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th>{b.skupaj}</th>
                  <td />
                  {poraba.skupajMeseci.map((z, i) => (
                    <td key={String(i)} className="stevilka">
                      <strong>{znesek(z)}</strong>
                    </td>
                  ))}
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
        {poraba.odprtMesec !== null && <p className="drobno">{b.odprtMesec(mesecZaPrikaz(poraba.odprtMesec))}</p>}
      </section>
      {odprto !== null && (
        <KnjizbeProjekta
          opis={odprto.opis}
          knjizbe={odprto.knjizbe}
          naZapri={() => {
            setOdprto(null);
          }}
        />
      )}
    </>
  );
}
